import { extract } from 'query-string';
import { BRAND_SEARCH_ENDPOINT, SEARCH_ENDPOINT, STORE_SEARCH_ENDPOINT } from './routeHelpers';
import { designerMode, productMode, storeMode } from './productListModes';
import Departments from '../common/models/Departments';

const womenAccessoriesIds = ['82'];
const womenBeltsIds = ['91'];
const womenBrasIds = ['17'];
const womenClothingIds = ['2'];
const womenGlovesIds = ['83'];
const womenHatsIds = ['89'];
const womenJeansIds = ['10'];
const womenShoesIds = ['108', '110', '111', '112', '114', '121', '120', '122'];
const womenRingsIds = ['74'];
const womenJewelryIds = ['69'];

const menAccessoriesIds = ['178'];
const menBeltsIds = ['187'];
const menClothingIds = ['125'];
const menGlovesIds = ['179'];
const menHatsIds = ['185'];
const menJeansIds = ['126'];
const menShirtsIds = ['148'];
const menShoesIds = ['202', '204', '205', '206', '207', '210'];
const menRingsIds = ['171'];
const menJewelryIds = ['167'];

const juniorKidsDepartmentIds = ['385', '399'];
const juniorKidsClothingIds = ['388', '389', '390', '396', '395', '397', '402', '409', '410', '408', '403', '411'];
const juniorKidsShoesIds = ['398', '412'];

const unisexJuniorShoes = ['425'];

const babiesDepartmentIds = ['348', '361'];
const babiesClothingIds = ['351', '352', '358', '353', '364', '370', '365'];
const babiesShoesIds = ['360', '372'];

const unisexBabiesShoes = ['384'];

const unisexAccessoriesIds = ['224'];
const unisexClothingIds = ['293'];
const unisexHatsIds = ['225'];
const unisexShoesIds = ['298'];
const unisexRingsIds = ['248'];

const allAccessoriesIds = [].concat(womenAccessoriesIds, menAccessoriesIds, unisexAccessoriesIds);
const allBeltsIds = [].concat(womenBeltsIds, menBeltsIds);
const allBrasIds = [].concat(womenBrasIds);
const allClothingIds = [].concat(
  womenClothingIds,
  menClothingIds,
  unisexClothingIds,
  juniorKidsClothingIds,
  babiesClothingIds
);
const allGlovesIds = [].concat(womenGlovesIds, menGlovesIds);
const allHatsIds = [].concat(womenHatsIds, menHatsIds, unisexHatsIds);
const allJeansIds = [].concat(womenJeansIds, menJeansIds);
const allShoesIds = [].concat(
  womenShoesIds,
  menShoesIds,
  unisexShoesIds,
  juniorKidsShoesIds,
  babiesShoesIds,
  unisexBabiesShoes,
  unisexJuniorShoes
);
const allShirtsIds = [].concat(menShirtsIds);
const allRingsIds = [].concat(womenRingsIds, menRingsIds, unisexRingsIds);
const allJelweryIds = [].concat(womenJewelryIds, menJewelryIds);
const categoriesWithSizes = [].concat(allClothingIds, allShoesIds, allAccessoriesIds, allJelweryIds);

const womenCategoryId = '1';
const menCategoryId = '124';

const isShoesCategory = categoryId => allShoesIds.indexOf(categoryId) > -1;

const searchTreeForCategoryWithSizes = (categoryTree, currentCategoryId) => {
  if (!currentCategoryId) {
    // department level - no size filter
    return false;
  }

  if (categoriesWithSizes.indexOf(currentCategoryId) > -1) {
    return true;
  }
  if (!categoryTree || categoryTree.length === 0) {
    return false;
  }
  let checkedCategory = categoryTree[0];
  if (categoryTree.length > 1 && searchCategoryInTree(categoryTree[1], currentCategoryId)) {
    checkedCategory = categoryTree[1];
  }
  while (checkedCategory) {
    if (categoriesWithSizes.indexOf(checkedCategory.id) > -1) {
      return true;
    }
    if (!checkedCategory.sub || checkedCategory.sub.length === 0) {
      return false;
    }
    const currentCategory = checkedCategory.sub.find(category => category.id === currentCategoryId);
    if (currentCategory) {
      checkedCategory = currentCategory;
    } else {
      checkedCategory = checkedCategory.sub.find(category => category.sub);
    }
  }
  return false;
};

const searchCategoryInTree = (rootCategory, categoryId) => {
  if (rootCategory.id === categoryId) {
    return true;
  }
  return rootCategory.sub && rootCategory.sub.some(child => searchCategoryInTree(child, categoryId));
};

const isWomenCategory = (categoryTree, categoryId) => {
  if (searchCategoryInTree(categoryTree[0], categoryId)) {
    return categoryTree[0].id === womenCategoryId;
  } else if (categoryTree.length > 1) {
    return categoryTree[1].id === womenCategoryId;
  }
  return true;
};

function getDefaultSizeGuideTabId(categoryTree, currentCategoryId) {
  if (allJeansIds.includes(currentCategoryId)) {
    return ['tab_jeans'];
  }
  if (allBrasIds.includes(currentCategoryId)) {
    return ['tab_bras'];
  }
  if (allShirtsIds.includes(currentCategoryId)) {
    return ['tab_shirts'];
  }

  if (allClothingIds.includes(currentCategoryId)) {
    return ['tab_clothing'];
  }

  if (allShoesIds.includes(currentCategoryId)) {
    return ['tab_shoes'];
  }

  if (allRingsIds.includes(currentCategoryId)) {
    return ['tab_rings', 'tab_accessories'];
  }
  if (allBeltsIds.includes(currentCategoryId)) {
    return ['tab_belts', 'tab_accessories'];
  }
  if (allGlovesIds.includes(currentCategoryId)) {
    return ['tab_gloves', 'tab_accessories'];
  }
  if (allHatsIds.includes(currentCategoryId)) {
    return ['tab_hats', 'tab_accessories'];
  }

  if (!categoryTree) {
    return ['tab_clothing'];
  }

  let firstLevelCategory = categoryTree[0];
  if (categoryTree.length > 1 && searchCategoryInTree(categoryTree[1], currentCategoryId)) {
    firstLevelCategory = categoryTree[1];
  }

  const secondLevelCategory = firstLevelCategory.sub.find(cat => cat.sub);
  const thirdLevelCategory =
    secondLevelCategory &&
    secondLevelCategory.sub &&
    secondLevelCategory.sub.find(cat => cat.sub || cat.id === currentCategoryId);

  if (thirdLevelCategory && allBrasIds.includes(thirdLevelCategory.id)) {
    return ['tab_bras'];
  }

  if (secondLevelCategory) {
    const secondLevelId = secondLevelCategory.id;
    if (allClothingIds.includes(secondLevelId)) {
      return ['tab_clothing'];
    }
    if (allShoesIds.includes(secondLevelId)) {
      return ['tab_shoes'];
    }
    if (thirdLevelCategory && allRingsIds.includes(thirdLevelCategory.id)) {
      return ['tab_rings', 'tab_accessories'];
    }
    if (thirdLevelCategory && allBeltsIds.includes(thirdLevelCategory.id)) {
      return ['tab_belts', 'tab_accessories'];
    }
    if (thirdLevelCategory && allGlovesIds.includes(thirdLevelCategory.id)) {
      return ['tab_gloves', 'tab_accessories'];
    }
    if (thirdLevelCategory && allHatsIds.includes(thirdLevelCategory.id)) {
      return ['tab_hats', 'tab_accessories'];
    }
  }
  return ['tab_clothing'];
}

function getDefaultSizeGuideTabIdFromPath(path) {
  const ancestors = path.split('/');
  if (ancestors.indexOf('jeans') > -1) {
    return 'tab_jeans';
  } else if (ancestors.indexOf('underwear-nightwear') > -1 && ancestors[0] === 'women') {
    return 'tab_bras';
  } else if (ancestors.indexOf('shirts') > -1 && ancestors[0] === 'men') {
    return 'tab_shirts';
  } else if (ancestors.indexOf('shoes') > -1) {
    return 'tab_shoes';
  } else if (ancestors.indexOf('belts') > -1) {
    return 'tab_belts';
  } else if (ancestors.indexOf('gloves') > -1) {
    return 'tab_gloves';
  } else if (ancestors.indexOf('hats') > -1) {
    return 'tab_hats';
  } else if (ancestors.indexOf('rings') > -1) {
    return 'tab_rings';
  }
  return 'tab_clothing';
}

const removeLastSlashFromUrl = url => {
  if (url.indexOf('/?') !== -1 || url.substr(url.length - 1) === '/') {
    return url.replace(/\/([^/]*)$/, '');
  }
  return url;
};
const getLastPathVarUrl = url =>
  removeLastSlashFromUrl(url)
    .split('/')
    .pop()
    .split('?')[0];

export const buildSearchBrandProductsFromUrl = (fromUrl, brandId = null) => {
  const lastPathVarUrl = getLastPathVarUrl(fromUrl);
  let departmentId = '';
  let apiBrandId = null;
  if (Departments.getAllowedDepartmentNames().includes(lastPathVarUrl)) {
    departmentId = `/${lastPathVarUrl}`;
    const splitUrl = removeLastSlashFromUrl(fromUrl).split('/');
    apiBrandId = splitUrl[splitUrl.length - 2];
  } else {
    apiBrandId = brandId || lastPathVarUrl;
  }

  const extractedQuery = extract(fromUrl);
  const q = extractedQuery ? `?${extractedQuery}` : '';
  return `${BRAND_SEARCH_ENDPOINT}${apiBrandId}${departmentId}${q}`;
};

export const buildSearchStoreProductsFromUrl = fromUrl => {
  const lastPathVarUrl = getLastPathVarUrl(fromUrl);
  let departmentId = '';
  let storeName = null;
  let storeId = null;
  const splitUrl = removeLastSlashFromUrl(fromUrl).split('/');
  if (Departments.getAllowedDepartmentNames().includes(lastPathVarUrl)) {
    departmentId = `/${lastPathVarUrl}`;
    storeName = splitUrl[splitUrl.length - 3];
    storeId = splitUrl[splitUrl.length - 2];
  } else {
    storeName = splitUrl[splitUrl.length - 2];
    storeId = splitUrl[splitUrl.length - 1];
  }
  return `${STORE_SEARCH_ENDPOINT}${storeName}/${storeId}${departmentId}`;
};

export const buildSearchProductsFromUrl = (fromUrl, productListMode, catId = null) => {
  if (productListMode === designerMode) {
    return buildSearchBrandProductsFromUrl(fromUrl, catId);
  }

  if (productListMode === storeMode) {
    return buildSearchStoreProductsFromUrl(fromUrl);
  }

  const categoryId = catId || getLastPathVarUrl(fromUrl);
  const extractedQuery = extract(fromUrl);
  const q = extractedQuery ? `?${extractedQuery}` : '';
  const baseUrl = SEARCH_ENDPOINT;
  return `${baseUrl}${categoryId}${q}`;
};

export const mixpanelTabValue = tab => {
  const tabMixpanel = tab.replace('tab_', '');
  return tabMixpanel.charAt(0).toUpperCase() + tabMixpanel.slice(1);
};

export const splitSizes = array =>
  array.map(el => {
    const split = el.split('_');
    if (split.length === 1) {
      return split[0];
    }
    return split.length > 2 ? `${split[2]} ${split[1]}` : `${split[1]}`;
  });

export {
  categoriesWithSizes,
  getDefaultSizeGuideTabId,
  getDefaultSizeGuideTabIdFromPath,
  isShoesCategory,
  isWomenCategory,
  searchTreeForCategoryWithSizes,
  searchCategoryInTree,
  menCategoryId,
  womenCategoryId
};
